/**
 * useSop Hook (Feature #54)
 *
 * React hook for managing SOP (Standard Operating Procedure) state:
 * workflow patterns, generated SOPs, and generation actions.
 *
 * @module useSop
 */

'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';

import { apiClient } from '@/lib/api-client';

// ============================================================================
// TYPES
// ============================================================================

export interface WorkflowPattern {
  actionSequence: string[];
  resourceTypes: string[];
  frequency: number;
  patternHash: string;
}

export interface SOPStep {
  number: number;
  action: string;
  tip?: string;
}

export interface GeneratedSOP {
  title: string;
  purpose: string;
  prerequisites: string[];
  steps: SOPStep[];
  bestPractices: string[];
  patternHash: string;
}

export interface SOPListItem {
  id: string;
  title: string;
  sourcePath: string;
  createdAt: string;
}

export interface UseSopReturn {
  patterns: WorkflowPattern[];
  sops: SOPListItem[];
  activeSOP: GeneratedSOP | null;
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  loadPatterns: () => Promise<void>;
  generateSOP: (pattern: WorkflowPattern) => Promise<void>;
  loadSOPs: () => Promise<void>;
  fetchSOP: (sopId: string) => Promise<void>;
  setActiveSOP: (sop: GeneratedSOP | null) => void;
  clearError: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for SOP management (Feature #54)
 *
 * @example
 * const {
 *   patterns,
 *   sops,
 *   loadPatterns,
 *   generateSOP,
 *   loadSOPs,
 * } = useSop();
 */
export function useSop(): UseSopReturn {
  const t = useTranslations('chatbot.sop');
  const [patterns, setPatterns] = useState<WorkflowPattern[]>([]);
  const [sops, setSops] = useState<SOPListItem[]>([]);
  const [activeSOP, setActiveSOP] = useState<GeneratedSOP | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load workflow patterns from audit log analysis
   */
  const loadPatterns = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<{ patterns: WorkflowPattern[] }>(
        '/ai-chatbot/sop/patterns'
      );
      setPatterns(response.data.patterns);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorLoadingPatterns'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  /**
   * Load list of generated SOPs
   */
  const loadSOPs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<{ sops: SOPListItem[] }>('/ai-chatbot/sop/list');
      setSops(response.data.sops);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorLoadingSops'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  /**
   * Generate an SOP from a workflow pattern
   */
  const generateSOP = useCallback(
    async (pattern: WorkflowPattern) => {
      setIsGenerating(true);
      setError(null);
      try {
        const response = await apiClient.post<GeneratedSOP>('/ai-chatbot/sop/generate', {
          actionSequence: pattern.actionSequence,
          resourceTypes: pattern.resourceTypes,
          frequency: pattern.frequency,
          patternHash: pattern.patternHash,
        });
        setActiveSOP(response.data);
        // Refresh the SOP list after generating
        await loadSOPs();
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to generate SOP';
        // Check if it's the "already exists" error
        if (errorMsg.includes('already exists')) {
          setError(t('errorAlreadyExists'));
        } else {
          setError(errorMsg);
        }
      } finally {
        setIsGenerating(false);
      }
    },
    [loadSOPs, t]
  );

  /**
   * Fetch full SOP details by ID
   */
  const fetchSOP = useCallback(async (sopId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<GeneratedSOP>(`/ai-chatbot/sop/${sopId}`);
      setActiveSOP(response.data);
    } catch {
      setError(t('errorLoadingDetails'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  /**
   * Clear any error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    patterns,
    sops,
    activeSOP,
    isLoading,
    isGenerating,
    error,
    loadPatterns,
    generateSOP,
    loadSOPs,
    fetchSOP,
    setActiveSOP,
    clearError,
  };
}
